import type {ReactNode, ThHTMLAttributes, TdHTMLAttributes, HTMLAttributes} from 'react'
import {cn} from '../../lib/utils'

export function DataTable({children}: {children: ReactNode}) {
    return (
        <div className='border border-foreground/10 overflow-x-auto'>
            <table className='w-full border-collapse font-mono text-xs'>{children}</table>
        </div>
    )
}

function Head({children}: {children: ReactNode}) {
    return (
        <thead>
            <tr className='bg-surface-container-high text-foreground/60 text-left'>{children}</tr>
        </thead>
    )
}

type HeadCellProps = ThHTMLAttributes<HTMLTableCellElement>

function HeadCell({className, children, ...rest}: HeadCellProps) {
    return (
        <th
            {...rest}
            className={cn(
                'px-4 py-2.5 border-b border-foreground/10 font-normal tracking-widest uppercase',
                className
            )}
        >
            {children}
        </th>
    )
}

type RowProps = HTMLAttributes<HTMLTableRowElement>

function Row({className, children, ...rest}: RowProps) {
    return (
        <tr
            {...rest}
            className={cn(
                'border-b border-foreground/[0.06] hover:bg-surface-container-low transition-colors',
                className
            )}
        >
            {children}
        </tr>
    )
}

type CellProps = TdHTMLAttributes<HTMLTableCellElement>

function Cell({className, children, ...rest}: CellProps) {
    return (
        <td
            {...rest}
            className={cn('px-4 py-2', className)}
        >
            {children}
        </td>
    )
}

function Foot({children}: {children: ReactNode}) {
    return (
        <tfoot>
            <tr className='bg-surface-container-high'>{children}</tr>
        </tfoot>
    )
}

DataTable.Head = Head
DataTable.HeadCell = HeadCell
DataTable.Row = Row
DataTable.Cell = Cell
DataTable.Foot = Foot

export interface ParamRowProps {
    readonly label: string
    readonly value: string
    /** Standards reference column. Use "—" for "no reference". */
    readonly ref_: string
}

/**
 * Canonical 3-column "label / value / standards-reference" row used by every
 * inspector params table (foni, arrow, future). Lives next to DataTable
 * because that's its only dependency.
 */
export function ParamRow({label, value, ref_}: ParamRowProps) {
    return (
        <Row>
            <Cell className='text-foreground/70'>{label}</Cell>
            <Cell className='text-foreground tabular-nums font-medium'>{value}</Cell>
            <Cell className='text-primary'>{ref_}</Cell>
        </Row>
    )
}
